import request from 'graphql-request';

import * as constants from '../constants';
import { ConfigService } from '../config';
import type { PhotoVogueService } from './service.interface';
import type {
  BatchResult,
  DailyAssignment,
  Photo,
  PhotoBatchPatch,
  PhotoVogueFilterOptions,
  Photographer,
  PhotographerDetails,
  PhotographerQueryParams,
  PhotographersResponse,
  PhotoModerationPatch,
  PhotoPatch,
  PhotoQueryParams,
  PhotosResponse,
} from './types';
import {
  GET_ALL_CATEGORIES,
  GET_ALL_COUNTRIES,
  GET_ALL_PHOTOGRAPHER_STATES,
  GET_ALL_TAGS,
  GET_ALL_THEMES,
  GET_ALL_PHOTO_STATES,
  GET_ALL_PHOTO_TYPES,
  GET_PHOTOGRAPHER_BY_ID,
  GET_PHOTO_BY_ID,
  SEARCH_ADMIN_PHOTOGRAPHERS,
  SEARCH_ADMIN_PHOTOS,
} from './queries.graphql';
import {
  mapPhoto,
  mapAdminDailyPhoto,
  mapPhotographer,
  type ApiAdminDailyPhoto,
  type ApiPhotoNode,
  type ApiPhotographerNode,
} from './mappers';
import {
  buildAdminPhotoBatchRequest,
  parsePhotoBatchResult,
} from './bulk-actions';
import {
  buildAdminPhotographerSearchInput,
  buildAdminPhotoSearchInput,
} from './admin-search-inputs';

interface DictNode {
  id: number | string;
  name?: string;
  text?: string;
}

interface DictConnection {
  edges: { node: DictNode }[];
  pageInfo?: {
    endCursor?: string | null;
    hasNextPage: boolean;
  } | null;
}

type DictResponse = Record<string, DictConnection>;

type DictKey =
  | 'photoStates'
  | 'photographerStates'
  | 'photoTypes'
  | 'categories'
  | 'themes'
  | 'tags'
  | 'countries';

const toDictMap = (edges: { node: DictNode }[]): Map<string, string> => {
  const map = new Map<string, string>();
  edges.forEach(({ node }) => {
    const label = node.name ?? node.text;
    if (label) map.set(label, String(node.id));
  });
  return map;
};

interface AdminSearchResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
}

/**
 * Step 2 implementation of `PhotoVogueService` that calls `photovogue-api` directly.
 * Reads use typed, admin-only GraphQL search queries plus dictionaries; writes use
 * the admin REST endpoints. Both send the staff Okta bearer this app already holds,
 * which `photovogue-api` verifies and authorizes.
 *
 * Filter values, counts, ordering, and pagination are all resolved by the API before
 * the current page is returned. `_ids` therefore always represents the current page.
 *
 * Other integration notes:
 * - Bulk moderation uses the canonical batch endpoint and always supplies each
 *   photo's complete type set so unrelated Best/Daily associations are preserved.
 * - Pic of the Day uses the protected legacy-compatible Daily REST collection,
 *   backed canonically by type-3 `photos_photo_types` associations.
 */
export class HttpPhotoVogueService implements PhotoVogueService {
  private photoStates?: Map<string, string>;
  private photographerStates?: Map<string, string>;
  private photoTypes?: Map<string, string>;
  private categories?: Map<string, string>;
  private themes?: Map<string, string>;
  private tags?: Map<string, string>;
  private countries?: Map<string, string>;

  private authHeaders(): Record<string, string> {
    const token = sessionStorage.getItem(constants.TOKEN_STORAGE_KEY) ?? '';
    return { Authorization: `Bearer ${token}` };
  }

  private gql<T>(
    query: string,
    variables: Record<string, unknown> = {},
  ): Promise<T> {
    return request<T>(
      ConfigService.PHOTOVOGUE_GRAPHQL_ENDPOINT,
      query,
      variables,
      this.authHeaders(),
    );
  }

  private async rest(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<{ status: number; body: unknown }> {
    const response = await fetch(
      `${ConfigService.PHOTOVOGUE_REST_ENDPOINT}${path}`,
      {
        method,
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: body === undefined ? undefined : JSON.stringify(body),
      },
    );
    const parsed = await response.json().catch(() => null);
    return { status: response.status, body: parsed };
  }

  private async loadDict(
    key: DictKey,
    query: string,
    field: string,
  ): Promise<Map<string, string>> {
    const cached = this[key];
    if (cached) return cached;
    const edges: { node: DictNode }[] = [];
    let after: string | undefined;
    do {
      const data = await this.gql<DictResponse>(query, {
        first: 500,
        after,
      });
      const connection = data[field];
      edges.push(...(connection?.edges ?? []));
      after = connection?.pageInfo?.hasNextPage
        ? (connection.pageInfo.endCursor ?? undefined)
        : undefined;
      if (connection?.pageInfo?.hasNextPage && !after) {
        throw new Error(`Missing end cursor while loading ${field}`);
      }
    } while (after);
    const map = toDictMap(edges);
    this[key] = map;
    return map;
  }

  private loadPhotoStates() {
    return this.loadDict(
      'photoStates',
      GET_ALL_PHOTO_STATES,
      'getAllPhotoStates',
    );
  }
  private loadPhotographerStates() {
    return this.loadDict(
      'photographerStates',
      GET_ALL_PHOTOGRAPHER_STATES,
      'getAllPhotographerStates',
    );
  }
  private loadPhotoTypes() {
    return this.loadDict('photoTypes', GET_ALL_PHOTO_TYPES, 'getAllPhotoType');
  }
  private loadCategories() {
    return this.loadDict('categories', GET_ALL_CATEGORIES, 'getAllCategories');
  }
  private loadThemes() {
    return this.loadDict('themes', GET_ALL_THEMES, 'getAllThemes');
  }
  private loadTags() {
    return this.loadDict('tags', GET_ALL_TAGS, 'getAllTags');
  }
  private loadCountries() {
    return this.loadDict('countries', GET_ALL_COUNTRIES, 'getAllCountries');
  }

  async getFilterValues(
    field: keyof PhotoVogueFilterOptions,
  ): Promise<string[]> {
    const loaders = {
      countries: () => this.loadCountries(),
      categories: () => this.loadCategories(),
      themes: () => this.loadThemes(),
      tags: () => this.loadTags(),
    };
    return [...(await loaders[field]()).keys()].sort();
  }

  async getFilterOptions(): Promise<PhotoVogueFilterOptions> {
    const [categories, themes, tags, countries] = await Promise.all([
      this.loadCategories(),
      this.loadThemes(),
      this.loadTags(),
      this.loadCountries(),
    ]);
    return {
      categories: [...categories.keys()].sort(),
      themes: [...themes.keys()].sort(),
      tags: [...tags.keys()].sort(),
      countries: [...countries.keys()].sort(),
    };
  }

  async getPhotos(params: PhotoQueryParams): Promise<PhotosResponse> {
    const { filters } = params;
    const [states, types, categories, themes, tags] = await Promise.all([
      filters.states?.length ? this.loadPhotoStates() : undefined,
      filters.type || filters.excludeType ? this.loadPhotoTypes() : undefined,
      filters.category ? this.loadCategories() : undefined,
      filters.themes?.length ? this.loadThemes() : undefined,
      filters.tags?.length ? this.loadTags() : undefined,
    ]);
    const input = buildAdminPhotoSearchInput(params, {
      states,
      types,
      categories,
      themes,
      tags,
    });
    const data = await this.gql<{
      searchAdminPhotos: AdminSearchResult<ApiPhotoNode>;
    }>(SEARCH_ADMIN_PHOTOS, { input });
    const pageItems = data.searchAdminPhotos.items.map(mapPhoto);

    return {
      data: pageItems,
      _ids: pageItems.map((photo) => photo.id),
      count: data.searchAdminPhotos.totalItems,
    };
  }

  async getPhoto(id: string): Promise<Photo> {
    const data = await this.gql<{ getPhotoById: ApiPhotoNode }>(
      GET_PHOTO_BY_ID,
      {
        input: { id: Number(id) },
      },
    );
    return mapPhoto(data.getPhotoById);
  }

  async moderatePhoto(id: string, patch: PhotoModerationPatch): Promise<Photo> {
    const states = await this.loadPhotoStates();
    const stateId = states.get(patch.state);
    if (!stateId) throw new Error(`Unknown photo state "${patch.state}"`);
    const { status } = await this.rest('PATCH', `/admin/photos/${id}`, {
      state_id: Number(stateId),
    });
    if (status < 200 || status >= 300) {
      throw new Error(`Failed to moderate photo ${id} (HTTP ${status})`);
    }
    return this.getPhoto(id);
  }

  async patchPhotos(patches: PhotoBatchPatch[]): Promise<BatchResult> {
    const [states, types] = await Promise.all([
      this.loadPhotoStates(),
      this.loadPhotoTypes(),
    ]);
    const body = buildAdminPhotoBatchRequest(
      patches,
      (state) => states.get(state),
      (type) => types.get(type),
    );
    const { status, body: responseBody } = await this.rest(
      'PATCH',
      '/admin/photos',
      body,
    );
    return parsePhotoBatchResult(status, responseBody);
  }

  async updatePhoto(id: string, patch: PhotoPatch): Promise<Photo> {
    const body: Record<string, unknown> = {};
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.tags !== undefined) {
      body.tags = patch.tags.map((text) => ({ text }));
    }
    if (patch.category !== undefined) {
      const categories = await this.loadCategories();
      const categoryId = categories.get(patch.category);
      if (categoryId) body.category_id = Number(categoryId);
    }
    if (patch.state !== undefined) {
      const states = await this.loadPhotoStates();
      const stateId = states.get(patch.state);
      if (stateId) body.state_id = Number(stateId);
    }
    if (patch.types !== undefined) {
      const types = await this.loadPhotoTypes();
      body.types = patch.types
        .map((name) => types.get(name))
        .filter((typeId): typeId is string => Boolean(typeId))
        .map((typeId) => ({ id: Number(typeId) }));
    }
    // `themes` is not accepted by AdminPhotoPatchDto — intentionally not sent.
    const { status } = await this.rest('PATCH', `/admin/photos/${id}`, body);
    if (status < 200 || status >= 300) {
      throw new Error(`Failed to update photo ${id} (HTTP ${status})`);
    }
    return this.getPhoto(id);
  }

  async getPhotographers(
    params: PhotographerQueryParams,
  ): Promise<PhotographersResponse> {
    const { filters } = params;
    const [states, countries] = await Promise.all([
      filters.states?.length ? this.loadPhotographerStates() : undefined,
      filters.country ? this.loadCountries() : undefined,
    ]);
    const input = buildAdminPhotographerSearchInput(params, {
      states,
      countries,
    });
    const data = await this.gql<{
      searchAdminPhotographers: AdminSearchResult<ApiPhotographerNode>;
    }>(SEARCH_ADMIN_PHOTOGRAPHERS, { input });
    const pageItems = data.searchAdminPhotographers.items.map(mapPhotographer);

    return {
      data: pageItems,
      _ids: pageItems.map((photographer) => photographer.id),
      count: data.searchAdminPhotographers.totalItems,
    };
  }

  async getPhotographerDetails(id: string): Promise<PhotographerDetails> {
    const { status, body } = await this.rest(
      'GET',
      `/admin/photographers/${id}/details`,
    );
    const result = body as { data?: PhotographerDetails } | null;
    if (status < 200 || status >= 300 || !result?.data) {
      throw new Error('Unable to load photographer details');
    }
    return result.data;
  }

  async getPhotographer(id: string): Promise<Photographer> {
    const data = await this.gql<{ getPhotographerById: ApiPhotographerNode }>(
      GET_PHOTOGRAPHER_BY_ID,
      { input: { id: Number(id) } },
    );
    return mapPhotographer(data.getPhotographerById);
  }

  async deletePhotographer(id: string): Promise<void> {
    const photographerId = Number(id);
    if (
      !/^\d+$/.test(id) ||
      !Number.isInteger(photographerId) ||
      photographerId <= 0 ||
      photographerId > 2147483647
    ) {
      throw new Error('Invalid photographer ID');
    }
    const data = await this.gql<{ deletePhotographer: string }>(
      `mutation DeletePhotographer($id: DeletePhotographerInput!) {
        deletePhotographer(id: $id)
      }`,
      { id: { id: photographerId } },
    );
    if (data.deletePhotographer !== 'Photographer remove') {
      throw new Error('Unable to delete photographer');
    }
  }

  async moderatePhotographer(
    id: string,
    state: Photographer['state'],
  ): Promise<Photographer> {
    const states = await this.loadPhotographerStates();
    const stateId = states.get(state);
    if (!stateId) throw new Error(`Unknown photographer state "${state}"`);
    const { status } = await this.rest('PATCH', `/admin/photographers/${id}`, {
      state_id: Number(stateId),
    });
    if (status < 200 || status >= 300) {
      throw new Error(`Failed to moderate photographer ${id} (HTTP ${status})`);
    }
    return this.getPhotographer(id);
  }

  async updatePhotographerSpotlight(
    id: string,
    spotlight: boolean,
  ): Promise<Photographer> {
    const { status } = await this.rest('PATCH', `/admin/photographers/${id}`, {
      isGold: spotlight,
    });
    if (status < 200 || status >= 300) {
      throw new Error(
        `Failed to update photographer spotlight ${id} (HTTP ${status})`,
      );
    }
    return this.getPhotographer(id);
  }

  async getDailies(
    dateFrom: string,
    dateTo: string,
  ): Promise<DailyAssignment[]> {
    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    const { status, body } = await this.rest(
      'GET',
      `/admin/daily?${query.toString()}`,
    );
    if (status < 200 || status >= 300) {
      throw new Error(
        `Failed to load Pic of the Day calendar (HTTP ${status})`,
      );
    }
    const items = (body as { items?: ApiAdminDailyPhoto[] } | null)?.items;
    if (!Array.isArray(items)) {
      throw new Error('Invalid Pic of the Day response');
    }
    return items.map((item) => ({
      date: item.daily_date,
      photo: mapAdminDailyPhoto(item),
    }));
  }

  async setDaily(date: string, photoId: string): Promise<DailyAssignment> {
    const { status, body } = await this.rest('PUT', '/admin/daily', {
      date,
      id: Number(photoId),
      add: true,
    });
    if (status < 200 || status >= 300) {
      throw new Error(`Failed to set Pic of the Day (HTTP ${status})`);
    }
    const photo = body as ApiAdminDailyPhoto | null;
    if (!photo || String(photo.id) !== photoId || photo.daily_date !== date) {
      throw new Error('Invalid Pic of the Day assignment response');
    }
    return { date, photo: mapAdminDailyPhoto(photo) };
  }

  async removeDaily(date: string, photoId?: string): Promise<void> {
    const { status } = await this.rest('DELETE', '/admin/daily', {
      date,
      ...(photoId ? { id: Number(photoId) } : {}),
      remove: true,
    });
    if (status < 200 || status >= 300) {
      throw new Error(`Failed to remove Pic of the Day (HTTP ${status})`);
    }
  }
}
